import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail,
  Phone,
  Calendar,
  Car,
  Flag,
  Trash2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Search
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const AdminMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);
  const [search, setSearch] = useState('');

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/members`, { headers: authHeaders() });
      setMembers(res.data);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const toggleExpand = async (memberId) => {
    if (expanded === memberId) {
      setExpanded(null);
      return;
    }
    setExpanded(memberId);
    if (!detail[memberId]) {
      setLoadingDetail(memberId);
      try {
        const res = await axios.get(`${API_URL}/api/admin/members/${memberId}`, {
          headers: authHeaders()
        });
        setDetail((d) => ({ ...d, [memberId]: res.data }));
      } catch {
        toast.error('Failed to load member details');
      } finally {
        setLoadingDetail(null);
      }
    }
  };

  const handleDelete = async (memberId, email) => {
    if (!window.confirm(`Delete member ${email}? This also removes their vehicles.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/members/${memberId}`, { headers: authHeaders() });
      toast.success('Member deleted');
      setExpanded(null);
      fetchMembers();
    } catch {
      toast.error('Failed to delete member');
    }
  };

  const filtered = members.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      m.email?.toLowerCase().includes(q) ||
      m.name?.toLowerCase().includes(q) ||
      m.phone?.toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-members">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6]">MEMBERS</h1>
          <p className="text-[#A19D98] text-sm mt-1">
            {members.length} total members — the rally family.
          </p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A19D98]" size={16} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / email / phone"
            data-testid="members-search-input"
            className="bg-[#141414] border-[#2A2A2A] text-[#F2EDE6] pl-10"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-[#E8651A] animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-[#A19D98]">No members found.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => (
            <div
              key={m.id}
              data-testid={`member-row-${m.id}`}
              className="bg-[#141414] border border-[#2A2A2A]"
            >
              <button
                onClick={() => toggleExpand(m.id)}
                data-testid={`member-expand-${m.id}`}
                className="w-full p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-left hover:bg-[#1a1a1a] transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <h3 className="font-['Oswald'] text-lg text-[#F2EDE6] truncate">
                    {m.name || '(no name)'}
                  </h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#A19D98] mt-1">
                    <span className="flex items-center gap-1">
                      <Mail size={12} /> {m.email}
                    </span>
                    {m.phone && (
                      <span className="flex items-center gap-1">
                        <Phone size={12} /> {m.phone}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      Joined {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1 text-[#A19D98] text-sm">
                    <Car size={14} /> {m.vehicles_count}
                  </div>
                  <div className="flex items-center gap-1 text-[#A19D98] text-sm">
                    <Flag size={14} /> {m.registrations_count}
                  </div>
                  {expanded === m.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </button>

              {expanded === m.id && (
                <div
                  data-testid={`member-detail-${m.id}`}
                  className="border-t border-[#2A2A2A] p-5 bg-[#0A0A0A]"
                >
                  {loadingDetail === m.id ? (
                    <Loader2 className="w-6 h-6 text-[#E8651A] animate-spin mx-auto" />
                  ) : (
                    <div className="space-y-6">
                      {/* Vehicles */}
                      <div>
                        <h4 className="font-['Oswald'] text-sm text-[#E8651A] tracking-wider mb-3">
                          GARAGE ({detail[m.id]?.vehicles?.length || 0})
                        </h4>
                        {detail[m.id]?.vehicles?.length ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {detail[m.id].vehicles.map((v) => (
                              <div key={v.id} className="bg-[#141414] border border-[#2A2A2A] overflow-hidden">
                                {v.photo_base64 ? (
                                  <img src={v.photo_base64} alt="vehicle" className="w-full h-32 object-cover" />
                                ) : (
                                  <div className="w-full h-32 bg-[#1C1C1C] flex items-center justify-center">
                                    <Car className="w-8 h-8 text-[#2A2A2A]" />
                                  </div>
                                )}
                                <div className="p-3">
                                  <p className="font-['Oswald'] text-[#F2EDE6] text-sm tracking-wider">
                                    {v.year} {v.make} {v.model}
                                  </p>
                                  <p className="text-[#A19D98] text-xs">{v.color}</p>
                                  {v.mods && (
                                    <p className="text-[#A19D98] text-xs mt-1 italic truncate">{v.mods}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[#A19D98] text-sm italic">No vehicles yet.</p>
                        )}
                      </div>

                      {/* Registrations */}
                      <div>
                        <h4 className="font-['Oswald'] text-sm text-[#E8651A] tracking-wider mb-3">
                          REGISTRATIONS ({detail[m.id]?.registrations?.length || 0})
                        </h4>
                        {detail[m.id]?.registrations?.length ? (
                          <div className="space-y-2">
                            {detail[m.id].registrations.map((r) => (
                              <div key={r.id} className="bg-[#141414] border border-[#2A2A2A] p-3 flex justify-between items-center">
                                <div>
                                  <p className="text-[#F2EDE6] text-sm">{r.rally_name || 'Unknown rally'}</p>
                                  {r.car_year && (
                                    <p className="text-[#A19D98] text-xs">
                                      {r.car_year} {r.car_make} {r.car_model}
                                    </p>
                                  )}
                                </div>
                                <span className="text-[#A19D98] text-xs">
                                  {new Date(r.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[#A19D98] text-sm italic">No rally registrations yet.</p>
                        )}
                      </div>

                      {/* Payments */}
                      <div>
                        <h4 className="font-['Oswald'] text-sm text-[#E8651A] tracking-wider mb-3">
                          PAYMENTS ({detail[m.id]?.payments?.length || 0})
                        </h4>
                        {detail[m.id]?.payments?.length ? (
                          <div className="space-y-2">
                            {detail[m.id].payments.map((p) => (
                              <div key={p.session_id} className="bg-[#141414] border border-[#2A2A2A] p-3 flex justify-between items-center text-sm">
                                <span className="text-[#F2EDE6]">{p.rally_name || '—'}</span>
                                <span className="text-[#A19D98]">
                                  ${p.amount?.toFixed(2)} · {p.payment_status}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[#A19D98] text-sm italic">No payments.</p>
                        )}
                      </div>

                      {/* Danger zone */}
                      <div className="pt-4 border-t border-[#2A2A2A]">
                        <button
                          onClick={() => handleDelete(m.id, m.email)}
                          data-testid={`member-delete-${m.id}`}
                          className="text-red-400 hover:text-red-300 text-sm flex items-center gap-2"
                        >
                          <Trash2 size={14} />
                          Delete Member
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMembers;
